/**
 * CreateIssueButton — admin "Issue erstellen"-button inside an expanded
 * /events detail panel.
 *
 * Renders one of two states based on `existingIssueUrl`:
 *   - **No existing issue** — a button that POSTs to the per-type endpoint
 *     (`createFeedbackIssue` / `createErrorIssue`) on click. On success it
 *     shows a Snackbar with the issue number and a link; on
 *     `alreadyExisted=true` it shows an info Snackbar; on failure it shows
 *     an error Snackbar.
 *   - **Existing issue link** — renders the link directly (no API call,
 *     no Snackbar). This is the post-success state and the "second-click
 *     idempotency" UX from issue #333.
 */

import React, { useState } from 'react';
import { Alert, Button, Link, Snackbar, Stack } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { eventsApi } from '../../services/eventsApi';
import type { CreateIssueResponse } from '../../services/types';

type EventKind = 'feedback' | 'error';

interface Props {
  eventKind: EventKind;
  eventId: string;
  /** If set, the button is replaced by a Link to the existing issue. */
  existingIssueUrl?: string;
  existingIssueNumber?: number;
  /** Callback fired after a successful create so the parent can refetch. */
  onCreated?: (response: CreateIssueResponse) => void;
}

type SnackbarState =
  | { kind: 'success'; message: string; url: string }
  | { kind: 'info'; message: string; url: string }
  | { kind: 'error'; message: string }
  | null;

const CreateIssueButton: React.FC<Props> = ({
  eventKind,
  eventId,
  existingIssueUrl,
  existingIssueNumber,
  onCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);

  if (existingIssueUrl) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Link
          href={existingIssueUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          GitHub-Issue {existingIssueNumber ? `#${existingIssueNumber}` : ''}
        </Link>
        <OpenInNewIcon fontSize="inherit" />
      </Stack>
    );
  }

  const handleClick = async () => {
    setLoading(true);
    try {
      const result = await (eventKind === 'feedback'
        ? eventsApi.createFeedbackIssue(eventId)
        : eventsApi.createErrorIssue(eventId));

      const issueLabel = `#${result.number}`;
      setSnackbar({
        kind: result.alreadyExisted ? 'info' : 'success',
        message: result.alreadyExisted
          ? `Issue ${issueLabel} wurde bereits erstellt.`
          : `Issue ${issueLabel} erstellt.`,
        url: result.url,
      });
      onCreated?.(result);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Issue-Service nicht erreichbar.';
      setSnackbar({ kind: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={loading}
      >
        {loading ? 'Erstelle Issue …' : 'GitHub-Issue erstellen'}
      </Button>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.kind}
            onClose={() => setSnackbar(null)}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
            {(snackbar.kind === 'success' || snackbar.kind === 'info') && (
              <>
                {' — '}
                <Link
                  href={snackbar.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                  underline="always"
                >
                  oeffnen
                </Link>
              </>
            )}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </>
  );
};

export default CreateIssueButton;
